import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { ChartCard } from "../components/dashboard/ChartCard";
import { CollectionHeading } from "../components/CollectionHeading";
import { CollectionSearch } from "../components/CollectionSearch";
import { SheetsHealthBanner } from "../components/SheetsHealthBanner";
import { SheetsReconnectBanner } from "../components/SheetsReconnectBanner";
import { IndexList } from "../components/IndexList";
import { BrokenRuleBannerLoader } from "../components/BrokenRuleBanner";
import { PreviewBanner } from "../components/PreviewBanner";
import { ViewOnlyBanner } from "../components/ViewOnlyBanner";
import { activityPath } from "../lib/audit-history";
import { latestSuccessAttempt } from "../lib/after-auth";
import { shareTarget } from "../lib/guest-share";
import { useAuth } from "../lib/useAuth";
import {
  callerTabAccess,
  canManageMembers,
  HIDDEN_RECORD_MESSAGE,
  TabRule,
  ViewerRole,
} from "../lib/member-access";
import { cardGridClass, cardSpanClass } from "../lib/card-grid";
import { chartRowStatus } from "../lib/chart-row-status";
import { chartsForCollection } from "../lib/collection-charts";
import { storedChartsNeedFallback } from "../lib/chart-axis-quality";
import {
  suggestChartsFromSchema,
  type SuggestedChart,
} from "../lib/suggest-charts";
import type { Field } from "../lib/schema-field";
import type { ChartDto } from "../lib/generated/api-client";
import {
  PREVIEW_PARAM,
  previewIdFromLocation,
  rememberedPreviewName,
} from "../lib/preview-as";
import { usePreviewSession } from "../lib/preview-session";
import { useDataManagement } from "../lib/useDataManagement";
import { useDatasources } from "../lib/useDatasources";
import { hasHealthProblem } from "../lib/sheets-health";
import { parseSheetsLink } from "../lib/sheets-link";
import { useSheetsHealth } from "../lib/useSheetsHealth";

/** Either a persisted ChartDto or a schema heuristic stand-in. */
type CollectionChart = ChartDto | SuggestedChart;

export default function Collection() {
  const { datasourceId, collectionName } = useParams<{
    datasourceId: string;
    collectionName: string;
  }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewMemberId = previewIdFromLocation(location);
  const preview = usePreviewSession();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const { datasources } = useDatasources();
  const { guest, user } = useAuth();
  const isGuest = Boolean(guest) && !user;
  const datasource = datasources.find((ds) => ds.id === datasourceId);
  const demoMode = !!(datasource as any)?.isDemo;
  // MOSA-367 slice 1. A Viewer folds into the same readOnly flag the grid
  // already honours, so no edit control is rendered anywhere in the panel.
  // The API refuses the write either way; this only stops offering it.
  const viewerRole = (datasource as any)?.viewerRole as ViewerRole;
  const attempt = latestSuccessAttempt(datasource?.analyzeAttempts);
  const collection = attempt?.databaseCollections?.find(
    (c) => c.name === collectionName,
  );
  // Custom is per table. A View table uses the same read-only path as a Viewer.
  // A table the API left out, or a No access rule, is the hidden-tab case.
  const tabAccess = callerTabAccess({
    viewerRole,
    access: (datasource as any)?.access,
    tab: collectionName,
    tabRules: (datasource as any)?.tabRules as TabRule[] | undefined,
    collectionLevel:
      (collection as any)?.level ?? (collection as any)?.accessLevel,
  });
  const viewOnly = tabAccess === "view";
  // Preview uses the same read-only grid. The API also refuses a write that
  // carries the preview id. The controls stay off either way.
  const readOnly =
    !!(datasource as any)?.readOnly || demoMode || viewOnly || !!previewMemberId;
  const projectId = (datasource as any)?.projectId as string | undefined;
  const previewHere =
    !!previewMemberId &&
    preview.datasourceId === datasourceId &&
    preview.memberId === previewMemberId;
  const previewName =
    (previewMemberId && rememberedPreviewName(previewMemberId)) ||
    preview.view?.email ||
    "this member";
  const exitPreview = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(PREVIEW_PARAM);
    setSearchParams(next);
  };
  // An owner opens the members page. A guest Postgres card with no project
  // opens sign-in. A member has nobody to share with. Preview hides Share
  // too: this screen is the member's panel, not the owner tools.
  const shareHref =
    !previewMemberId && datasourceId
      ? shareTarget({
          id: datasourceId,
          isDemo: demoMode,
          type: String((datasource as { type?: string } | undefined)?.type ?? ""),
          projectId,
          viewerRole,
          isGuest,
        })
      : undefined;
  // Activity is the owner's list too (MOSA-368 slice 1, Q20). A member reads a
  // row's own history in the record drawer, where the API allows it.
  const activityHref =
    !previewMemberId && !demoMode && datasourceId && canManageMembers(viewerRole)
      ? activityPath(datasourceId)
      : undefined;
  // The sync state routes exist for Google Sheets datasources only (ARD 9.6).
  const sheetsSync =
    !demoMode && String((datasource as any)?.type) === "GOOGLE_SHEETS";

  const fields = collection?.fields;
  const schemaFields = (fields ?? []) as unknown as Field[];

  // Prefer charts /ui/generate already persisted. Fall back to the schema
  // heuristic when generate left none, every stored series is unplottable, or
  // the stored set charts a measure column as a category (Sample Orders after
  // #338: count by Quantity / Unit Price looked plottable and blocked fallback).
  const generatedCharts = useMemo(
    () => chartsForCollection(attempt as any, collectionName),
    [attempt, collectionName],
  );
  const suggestedCharts = useMemo(
    () =>
      suggestChartsFromSchema(
        collectionName,
        (fields ?? []) as unknown as Field[],
      ),
    [collectionName, fields],
  );
  const generatedAreWeak = useMemo(
    () =>
      storedChartsNeedFallback(
        generatedCharts,
        (fields ?? []) as unknown as Field[],
      ),
    [generatedCharts, fields],
  );

  // Which charts came back with nothing to draw. A card only learns this after
  // its own series query resolves, so it cannot be filtered before render: the
  // cards report back and the row re-lays out around the survivors. Without
  // this the omitted card left a null cell, and the donut kept a half-width
  // column beside a hole.
  const [unplottable, setUnplottable] = useState<Record<string, boolean>>({});
  const [useSuggestedFallback, setUseSuggestedFallback] = useState(false);

  useEffect(() => {
    setUseSuggestedFallback(false);
    setUnplottable({});
  }, [datasourceId, collectionName]);

  const preferSuggested =
    generatedCharts.length === 0 ||
    (generatedAreWeak && suggestedCharts.length > 0) ||
    (useSuggestedFallback && suggestedCharts.length > 0);

  const charts: CollectionChart[] = preferSuggested
    ? suggestedCharts
    : generatedCharts;

  const reportUnplottable = useCallback(
    (id: string) => (isUnplottable: boolean) =>
      setUnplottable((current) =>
        current[id] === isUnplottable
          ? current
          : { ...current, [id]: isUnplottable },
      ),
    [],
  );

  const plottableCharts = useMemo(
    () => charts.filter((chart: CollectionChart) => !unplottable[chart.id]),
    [charts, unplottable],
  );

  // Cards report only after their series query resolves. Treat a chart as
  // unplottable only once every id has a report, otherwise first paint would
  // flash the failed-draw copy while queries are still in flight.
  const allChartsReported =
    charts.length > 0 &&
    charts.every(
      (chart: CollectionChart) => typeof unplottable[chart.id] === "boolean",
    );
  const allUnplottable = allChartsReported && plottableCharts.length === 0;

  useEffect(() => {
    if (useSuggestedFallback) return;
    if (generatedCharts.length === 0) return;
    if (!allUnplottable) return;
    if (!suggestedCharts.length) return;
    setUseSuggestedFallback(true);
    setUnplottable({});
  }, [
    useSuggestedFallback,
    generatedCharts.length,
    allUnplottable,
    suggestedCharts.length,
  ]);

  const [searchHitsEpoch, setSearchHitsEpoch] = useState(0);
  const bumpSearchHits = useCallback(
    () => setSearchHitsEpoch((n) => n + 1),
    [],
  );
  const [searchResult, setSearchResult] = useState({
    total: 0,
    loading: false,
    stale: false,
  });
  const onSearchResult = useCallback(
    (result: { total: number; loading: boolean; stale?: boolean }) =>
      setSearchResult({ ...result, stale: !!result.stale }),
    [],
  );
  const {
    Manager,
    FormModal,
    openForm,
    title,
    total,
    loading,
    filterGroups,
    onFilterChange,
    sort,
    onSortChange,
    searchQuery,
    listPage,
    listPageSize,
    onListParamsChange,
    sync,
    reconnect,
  } = useDataManagement({
    datasourceId: datasourceId!,
    collectionName: collectionName!,
    schema: (fields ?? []) as unknown as Field[],
    demoMode,
    readOnly,
    sheetsSync,
    // Page owns the h1 above the chart row so chart card h2s stay in order.
    hideHeading: true,
    onSaved: bumpSearchHits,
    // Datasource arrives after the first paint. Following earlier strips `open`
    // while readOnly is still the demo default and the linked row never loads.
    rowLinks: !!datasource,
    shareHref,
    activityHref,
  });
  const phoneCanWrite = demoMode || !readOnly;

  // MOSA-409. Connection health for a Sheets panel. The read above is also
  // the check, so this is asked again whenever a read has just failed.
  const { health, setHealth } = useSheetsHealth({
    datasourceId,
    enabled: sheetsSync,
    errorEpoch: reconnect ? 1 : 0,
  });

  if (!datasource) {
    return (
      <div className="p-6 text-sm text-od-muted">
        That datasource is not in your workspace.
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="p-6 text-sm text-od-muted">
        This datasource has no analysed schema.
      </div>
    );
  }

  const previewBanner = previewMemberId ? (
    <PreviewBanner
      name={previewName}
      message={previewHere ? preview.error : null}
      onExit={exitPreview}
      className={phone ? "mx-3" : ""}
    />
  ) : null;

  if (!collection || tabAccess === "hidden") {
    return (
      <div className="flex flex-col gap-2.5">
        {previewBanner}
        <div className="p-6 text-sm text-od-muted" data-testid="hidden-tab">
          {HIDDEN_RECORD_MESSAGE}
        </div>
      </div>
    );
  }

  if (previewMemberId && previewHere && preview.error) {
    return <div className="p-3">{previewBanner}</div>;
  }

  const previewOmitsTab =
    previewHere &&
    !!preview.view &&
    !preview.view.tabs.some((tab) => tab.tab === collectionName);
  if (previewOmitsTab) {
    return (
      <div className="flex flex-col gap-2.5">
        {previewBanner}
        <div className="p-6 text-sm text-od-muted" data-testid="hidden-tab">
          {HIDDEN_RECORD_MESSAGE}
        </div>
      </div>
    );
  }

  const link = parseSheetsLink((datasource as any)?.connectionString);
  const spreadsheetId = link.ok ? link.spreadsheetId : null;
  const healthBanner =
    sheetsSync && health && hasHealthProblem(health) ? (
      <SheetsHealthBanner
        health={health}
        datasourceId={datasourceId!}
        spreadsheetId={spreadsheetId}
        onResolved={setHealth}
      />
    ) : null;
  // The health banner carries the same case with the right copy for this
  // caller, and a member gets no button there. Showing both would say it
  // twice, and the older banner offers a member a reconnect they cannot do
  // (QA finding D3 on MOSA-554).
  const legacyReconnect = reconnect && !healthBanner ? reconnect : null;

  const hasCharts = charts.length > 0;
  const showChartGrid = hasCharts && !allUnplottable;
  const chartStatus = hasCharts ? chartRowStatus({ declared: charts, unplottable }) : null;

  const banners = phone ? (
    <>
      {healthBanner && <div className="mx-3">{healthBanner}</div>}
      {legacyReconnect && (
        <SheetsReconnectBanner info={legacyReconnect} className="mx-3" />
      )}
      {previewBanner}
      {shareHref && projectId && (
        <BrokenRuleBannerLoader
          projectId={projectId}
          datasourceId={datasourceId!}
          className="mx-3"
        />
      )}
      {viewOnly && !previewMemberId && (
        <ViewOnlyBanner projectId={projectId} className="mx-3" />
      )}
    </>
  ) : (
    <>
      {healthBanner}
      {legacyReconnect && <SheetsReconnectBanner info={legacyReconnect} />}
      {previewBanner}
      {shareHref && projectId && (
        <BrokenRuleBannerLoader
          projectId={projectId}
          datasourceId={datasourceId!}
        />
      )}
      {viewOnly && !previewMemberId && <ViewOnlyBanner projectId={projectId} />}
    </>
  );

  const heading = (
    <CollectionHeading
      className={phone ? "px-3" : undefined}
      title={title}
      total={phone ? searchResult.total ?? 0 : total}
      loading={phone ? !!searchResult.loading : loading}
      demoMode={demoMode}
      shareHref={shareHref}
      activityHref={activityHref}
      sync={
        phone && sync && searchResult.stale ? { ...sync, stale: true } : sync
      }
      onCreate={!phone && !readOnly ? () => openForm() : undefined}
    />
  );

  const records = phone ? (
    <>
      <CollectionSearch
        datasourceId={datasourceId!}
        collectionName={collectionName!}
        schema={(fields ?? []) as unknown as Field[]}
        onEdit={
          phoneCanWrite
            ? (row) => openForm(row, { isUpdate: true })
            : undefined
        }
        onCreate={!readOnly ? () => openForm() : undefined}
        refreshEpoch={searchHitsEpoch}
        demoMode={demoMode}
        filterGroups={filterGroups}
        onFilterChange={onFilterChange}
        sort={sort}
        onSortChange={onSortChange}
        urlQuery={searchQuery}
        urlPage={listPage}
        urlPageSize={listPageSize}
        onListParamsChange={onListParamsChange}
        onResultChange={onSearchResult}
        previewMemberId={previewMemberId}
      />
      {phoneCanWrite && FormModal}
    </>
  ) : hasCharts ? (
    <div className="shrink-0">{Manager}</div>
  ) : (
    Manager
  );

  const chartRow = showChartGrid ? (
    // Every chart mounts, because a card is the only thing that can tell
    // us whether its series is plottable. The unplottable ones render null
    // and so add no grid cell; the row's columns and the span on the last
    // survivor are computed from the plottable ones, which is why a lone
    // remaining figure spans instead of sitting beside a hole.
    // After every card has reported unplottable the grid is dropped so
    // the page does not keep an empty row above the table.
    <div className={cardGridClass(plottableCharts.length)}>
      {charts.map((chart) => {
        // -1 while the card is still mounted but reporting unplottable. It
        // draws nothing, so it has no cell to place; say that here rather
        // than lean on `cardSpanClass(-1, n)` happening to return "".
        const plottableIndex = plottableCharts.findIndex(
          (plottable: CollectionChart) => plottable.id === chart.id,
        );

        return (
          <ChartCard
            key={chart.id}
            className={
              plottableIndex === -1
                ? ""
                : cardSpanClass(plottableIndex, plottableCharts.length)
            }
            onUnplottable={reportUnplottable(chart.id)}
            datasourceId={datasourceId!}
            name={chart.name}
            title={chart.title}
            description={chart.description}
            type={chart.type}
            groupBy={(chart as any).groupBy}
            bucket={(chart as any).bucket}
            measure={(chart as any).measure}
            collection={collectionName}
            filterGroups={filterGroups}
            schema={schemaFields}
          />
        );
      })}
    </div>
  ) : null;

  // Charts (or the compact empty/error line) sit above the table. Parent
  // gap-2.5 separates this block from the grid; no trailing fold spacer.
  const chartsBlock =
    chartRow || chartStatus ? (
      <section
        aria-label="Charts"
        className="flex shrink-0 flex-col gap-2"
      >
        {chartRow}
        {chartStatus && (
          <p className="text-sm text-od-muted">{chartStatus}</p>
        )}
      </section>
    ) : null;

  return (
    <div
      className={`flex min-h-full flex-col gap-2.5 ${
        hasCharts ? "" : "h-full min-h-0"
      }`}
    >
      {banners}
      {heading}
      {chartsBlock}
      {records}
      {!phone && (
        <IndexList
          indexes={collection.indexes}
          primaryFieldName={fields?.find((field) => field.primary)?.name}
        />
      )}
    </div>
  );
}
